import Main from "@components/Layout/Main";
import Seo from "@components/Seo";
import RichText from "@components/RichText";
import { graphql, PageProps } from "gatsby";
import React, { useEffect, useRef, useState } from "react";
import { DefaultPageContext } from "@typed/common";
import styled from "styled-components";
import { FlexBox } from "@styles";
import { Brand } from "@typed/categories";

const Iframe = styled.iframe<{ defaultHeight: number | null }>`
  min-height: ${({ defaultHeight }) => (defaultHeight ? `${defaultHeight}px` : "82vh")};
`;

const Wysiwyg = ({ data: { oldPages }, pageContext }: PageProps<Queries.OldPagesQuery, DefaultPageContext & { brand?: Brand }>) => {
  const pageContent = oldPages?.translation?.[0];
  const template = oldPages?.template ?? "max-w-prose";
  const [height, setheight] = useState("300px");

  useEffect(() => {
    const handleReceiveMessage = (event: { data: { eventName: any; payload: any } }) => {
      const eventName = event?.data?.eventName;
      const payload = event?.data?.payload;

      if (eventName === "SET_HEIGHT" && payload?.height) {
        setheight(`${payload.height}px`);
      }
    };

    window.addEventListener("message", handleReceiveMessage);
  }, []);

  const frame = useRef<HTMLIFrameElement>(null);

  if (!pageContent) return null;

  return (
    <Main title={pageContent?.title ?? ""} brand={pageContext?.brand}>
      <FlexBox direction="column" gap={1} paddingTop={1}>
        {pageContent?.content && <RichText body={pageContent?.content} template={template} />}

        {pageContent?.external_content_url && (
          <Iframe
            ref={frame}
            defaultHeight={pageContent?.external_content_height}
            height={height}
            src={pageContent.external_content_url}
            title={pageContent.title ?? ""}
            frameBorder={0}
            style={{ width: "100%" }}
          />
        )}
      </FlexBox>
    </Main>
  );
};

export default Wysiwyg;

export const query = graphql`
  query OldPages($slug: String) {
    oldPages(slug: { eq: $slug }) {
      id
      template
      page_metadata {
        title
        description
      }
      translation {
        external_content_url
        external_content_height
        title
        content
        language
      }
    }
  }
`;

const cleanSlug = (slug: string) =>
  slug
    .split("-")
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const Head = ({
  data: { oldPages },
  pageContext: { slug },
}: PageProps<Queries.OldPagesQuery, DefaultPageContext & { brand?: Brand }>) => {
  const translationTitle = oldPages?.translation?.find((t) => t?.language === "en")?.title;
  const meta_title = oldPages?.page_metadata?.title;
  const meta_description = oldPages?.page_metadata?.description;

  return <Seo title={meta_title || translationTitle || cleanSlug(slug)} description={meta_description ?? undefined} />;
};
